import { powerSyncCollectionOptions } from "@tanstack/powersync-db-collection";
import { createCollection } from "@tanstack/react-db";
import {
  BoardBasisSchema,
  ReservationSourceSchema,
  ReservationStatusSchema,
} from "@vidimo/shared";
import { z } from "zod";
import { AppSchema } from "@/lib/powersync-schema.generated";
import { collectionBase } from "./_factory";
import { nullishToNull } from "./nullish";
import { serverEnum, serverEnumOrNull } from "./server-enum";
import { syncedRow } from "./synced-row";

/**
 * The columns both schemas agree on, spelled once.
 *
 * A column added to the wire schema and forgotten here would be STRIPPED on
 * every `collection.update(...)` rather than rejected — the in-memory schema is
 * a plain `z.object`, so an unlisted key is silently dropped. Spreading one
 * object is what makes that impossible; the columns that genuinely differ stay
 * inline where they are declared, which is where the difference matters.
 */
const reservationColumns = {
  accessibilityNotes: z.string().nullable(),
  adults: z.number().nullable(),
  bookerGuestId: z.string(),
  bookingReference: z.string().nullable(),
  checkInAt: z.string().nullable(),
  checkInTime: z.string().nullable(),
  checkOutAt: z.string().nullable(),
  checkOutTime: z.string().nullable(),
  children: z.number().nullable(),
  createdAt: z.string(),
  // Folio currency + server-computed total. Nullable: legacy bookings have no
  // currency/total yet.
  currency: z.string().nullable(),
  dietaryNotes: z.string().nullable(),
  expectedArrivalEndTime: z.string().nullable(),
  expectedArrivalTime: z.string().nullable(),
  externalData: z.string().nullable(),
  externalId: z.string().nullable(),
  // The rules SNAPSHOT (`houseRulesAcceptedText`) is deliberately not synced;
  // see powersync/sync-config.yaml. Only the timestamp reaches the client.
  houseRulesAcceptedAt: z.string().nullable(),
  id: z.string(),
  infants: z.number().nullable(),
  integrationId: z.string().nullable(),
  integrationMappingId: z.string().nullable(),
  notes: z.string().nullable(),
  petNotes: z.string().nullable(),
  propertyId: z.string(),
  // All columns are `.nullable()` (not `.nullish()`): the deserialization
  // schema's input must be exactly the raw SQLite column types
  // (`ExtractedTable` = `T | null`, no `undefined`), and PowerSync always
  // materializes a client-schema column as `null` when empty. Same shape the
  // chat/guest collections use.
  sourceDetail: z.string().nullable(),
  // Staff's tourist-tax note. Full-sync members only: the operator projection
  // leaves it out, so an operator's rows hold null.
  taxNote: z.string().nullable(),
  totalAmountMinor: z.number().nullable(),
  updatedAt: z.string(),
};

// Two-schema pattern per the PowerSync × TanStack DB docs
// (https://tanstack.com/db/latest/docs/collections/powersync-collection):
//   - `deserializationSchema` (below) validates the raw SQLite row and
//     transforms it to the rich in-memory shape. Its INPUT types must be the
//     raw column types (text → string, integer → number) so it satisfies the
//     collection's `ExtractedTable` contract — hence `source`/`status` are
//     `z.string()` here and the enum is applied in the transform, not on input.
//   - `InMemoryReservationSchema` (the collection `schema`) is the rich shape
//     TanStack DB re-validates against on every insert/update. `hasPets` is a
//     boolean there; the default serializer maps it back to 0/1 on write.
export const SelectReservationSchema = syncedRow({
  ...reservationColumns,
  // An unknown board reads as unstated, which includes no meal.
  boardBasis: serverEnumOrNull(BoardBasisSchema),
  hasPets: z.number().nullable(),
  // Tolerant enum reads (see server-enum.ts): a rejected row drops out of
  // the collection, so a new server-side value must fall back, not hide the
  // reservation. Unknown source
  // reads as the designed catch-all; unknown status as the state that
  // claims least.
  source: serverEnum(ReservationSourceSchema, "other"),
  status: serverEnum(ReservationStatusSchema, "requested"),
}).transform((row) => ({
  ...nullishToNull(row),
  hasPets: row.hasPets === null ? null : row.hasPets === 1,
}));

// The rich in-memory shape TanStack DB re-validates against on every
// insert/update. Matches the deserialization output 1:1, so its inferred type
// equals `Reservation` and read sites are unchanged. `hasPets` must be the
// boolean the transform produces: typing it as the 0/1 integer would make
// every reservation update that leaves `hasPets` untouched throw
// SchemaValidationError (the whole merged row is re-parsed against this).
export const InMemoryReservationSchema = z.object({
  ...reservationColumns,
  boardBasis: BoardBasisSchema.nullable(),
  hasPets: z.boolean().nullable(),
  source: ReservationSourceSchema,
  status: ReservationStatusSchema,
});

export type Reservation = z.infer<typeof SelectReservationSchema>;

export const reservationsCollection = createCollection(
  powerSyncCollectionOptions({
    ...collectionBase("reservation"),
    deserializationSchema: SelectReservationSchema,
    schema: InMemoryReservationSchema,
    table: AppSchema.props.reservations,
  })
);
